import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../store/indexRedux";
import { fetchUserCompany, resetCompanyState } from "../store/companySlice";
import { fetchCompanies } from "../store/allCompanySlice";
import Loading from "../components/ui/Loading";
import RightEmptyArrow from "../components/svg/rightEmptyArrow";
import SearchInput from "../components/ui/SearchInput";
import { useTranslation } from "react-i18next";
import { getSessionFromCookie } from "../components/utils/cognito";
import { getWorkOSAccessToken } from "../components/utils/workos";
import { jwtDecode } from "jwt-decode";

const COMPANIES_PER_PAGE = 4;

const SelectCompanyPage: React.FC = () => {
  const { t } = useTranslation("SelectCompany");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { companies, loading } = useSelector((state: RootState) => state.allCompany);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  const session = getSessionFromCookie();
  const token = session?.getAccessToken().getJwtToken() || getWorkOSAccessToken();

  const isJwtToken = (token: string): boolean => {
    try {
      jwtDecode(token);
      return true;
    } catch {
      return false;
    }
  };

  let cognitoId: string | undefined;
  if (token && isJwtToken(token)) {
    try {
      const decodedToken: { sub: string } = jwtDecode(token);
      cognitoId = decodedToken.sub;
      console.log("Cognito ID:", cognitoId);
    } catch (error) {
      console.error("Error decoding JWT token:", error);
    }
  }
  const [searchTerm, setSearchTerm] = useState("");
  const [displayedCompanies, setDisplayedCompanies] = useState<typeof companies>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastCompanyElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  useEffect(() => {
    if (token) {
      dispatch(fetchCompanies({ token }));
      setFetchAttempted(true);
    }
  }, [dispatch, token]);

  useEffect(() => {
    localStorage.removeItem("selectedCompanyId");
    localStorage.removeItem("selectedUserCompany");
    dispatch(resetCompanyState());
  }, [dispatch]);

  useEffect(() => {
    if (companies.length > 0) {
      const sortedCompanies = [...companies].sort((a, b) => a.name.localeCompare(b.name));
      const newDisplayedCompanies = sortedCompanies.slice(0, page * COMPANIES_PER_PAGE);
      setDisplayedCompanies(newDisplayedCompanies);
      setHasMore(newDisplayedCompanies.length < sortedCompanies.length);
    }
  }, [companies, page]);

  const handleSelectCompany = (companyId: string | number) => {
    dispatch(resetCompanyState());
    localStorage.setItem("selectedCompanyId", companyId.toString());
    console.log("Selected company:", companyId);

    // Pass an object to fetchUserCompany
    dispatch(fetchUserCompany({ company_id: companyId, token: token ?? undefined }));
    navigate("/overview");
  };

  // if (loading && displayedCompanies.length === 0) {
  //   return <Loading />;
  // }

  const filteredCompanies = searchTerm
    ? companies
        .filter((company) => {
          const search = searchTerm.toLowerCase();
          const companyName = company.name.toLowerCase();
          const companyId = String(company.id).toLowerCase();

          return companyName.includes(search) || companyId.startsWith(search);
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    : displayedCompanies;

  useEffect(() => {
    if (token) {
      setFetchAttempted(true);
      dispatch(fetchCompanies({ token }));
    }
  }, [dispatch, token]);

  if (loading || !fetchAttempted) {
    return <Loading />;
  }
  if (!loading && fetchAttempted && companies.length === 0) {
    return (
      <div className="flex flex-col items-center mt-32 ">
        <div className="lg:w-[700px]">
          <div className="p-5 border border-[#FED7AA] rounded-md bg-[#FFEDD5] text-[#C2410C] text-sm">
            <p className="font-semibold mb-2"> {t("noCompanyTitle")}</p>
            <p className="mb-4">{t("noCompanyDescription")}</p>
            <NavLink
              to="/add-company"
              className="inline-block bg-[#C2410C] text-white py-2 px-4 rounded-md text-sm hover:bg-[#9A3412] transition-colors"
            >
              {t("addCompanyButton")}
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* <div className="lg:w-[700px]  mt-[7.8rem] mx-auto ">
        <NavLink
          to="/add-company"
          className="bg-primary-600 dark:bg-primary-800 text-white py-[0.78rem] px-[1.5rem] rounded-[0.25rem] text-[0.875rem]"
        >
          {t("addCompany")}
        </NavLink>
      </div> */}

      <div className="lg:w-[700px] border-[1px] dark:border-primary-800 mt-12 mb-[7.8rem] mx-auto rounded-lg">
        <div className="bg-primary-600 dark:bg-primary-800 px-8 py-7 text-white rounded-t-lg flex items-center justify-between">
          <h1 className="font-semibold text-xl">{t("selectCompany")}</h1>
          <NavLink
            to="/add-company"
            className="bg-primary-700 dark:bg-primary-900 text-white py-2 px-4 rounded text-sm font-semibold hover:bg-primary-800 transition-colors"
          >
            {t("addCompany")}
          </NavLink>
        </div>
        <div className="bg-white dark:bg-primary-900 px-8 p-7 rounded-b-lg">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("searchCompany")}
            className="mb-8"
          />
          <div className="max-h-[380px] overflow-y-auto" id="scrollableDiv">
            {filteredCompanies.map((company, index) => (
              <div
                className="flex justify-between items-center bg-gray-50 dark:bg-primary-950 rounded-[4px] border-[1px] dark:border-primary-800 px-6 py-4 mb-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-primary-900"
                key={company.id}
                onClick={() => handleSelectCompany(company.id)}
                ref={index === filteredCompanies.length - 1 ? lastCompanyElementRef : null}
              >
                <div>
                  <h2 className="text-primary-600 dark:text-white mb-[3px] font-semibold">{company.name}</h2>
                  <p className="text-primary-400 dark:text-primary-300">#{company.id}</p>
                </div>
                <div>
                  <button>
                    <RightEmptyArrow />
                  </button>
                </div>
              </div>
            ))}
            {loading && <Loading />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectCompanyPage;

// import React, { useEffect, useState, useRef, useCallback } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { NavLink, useNavigate } from "react-router-dom";
// import { RootState, AppDispatch } from "../store/indexRedux";
// import { fetchUserCompany, resetCompanyState } from "../store/companySlice";
// import { fetchCompanies } from "../store/allCompanySlice";
// import Loading from "../components/ui/Loading";
// import RightEmptyArrow from "../components/svg/rightEmptyArrow";
// import SearchInput from "../components/ui/SearchInput";
// import { useTranslation } from "react-i18next";
// import { getSessionFromCookie } from "../components/utils/cognito";
// import { jwtDecode } from "jwt-decode";

// const COMPANIES_PER_PAGE = 4;

// const SelectCompanyPage: React.FC = () => {
//   const { t } = useTranslation("SelectCompany");
//   const dispatch = useDispatch<AppDispatch>();
//   const navigate = useNavigate();
//   const { companies, loading } = useSelector((state: RootState) => state.allCompany);
//   const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
//   const prevLoading = useRef(loading);

//   const [fetchAttempted, setFetchAttempted] = useState(false);

//   const session = getSessionFromCookie();
//   const token = session?.getAccessToken().getJwtToken();

//   const isJwtToken = (token: string): boolean => {
//     try {
//       jwtDecode(token);
//       return true;
//     } catch {
//       return false;
//     }
//   };

//   let cognitoId: string | undefined;
//   if (token && isJwtToken(token)) {
//     try {
//       const decodedToken: { sub: string } = jwtDecode(token);
//       cognitoId = decodedToken.sub;
//       console.log("Cognito ID:", cognitoId);
//     } catch (error) {
//       console.error("Error decoding JWT token:", error);
//     }
//   }
//   const [searchTerm, setSearchTerm] = useState("");
//   const [displayedCompanies, setDisplayedCompanies] = useState<typeof companies>([]);
//   const [hasMore, setHasMore] = useState(true);
//   const [page, setPage] = useState(1);

//   const observer = useRef<IntersectionObserver | null>(null);
//   const lastCompanyElementRef = useCallback(
//     (node: HTMLDivElement | null) => {
//       if (loading) return;
//       if (observer.current) observer.current.disconnect();
//       observer.current = new IntersectionObserver((entries) => {
//         if (entries[0].isIntersecting && hasMore) {
//           setPage((prevPage) => prevPage + 1);
//         }
//       });
//       if (node) observer.current.observe(node);
//     },
//     [loading, hasMore]
//   );

//   useEffect(() => {
//     if (cognitoId && token) {
//       dispatch(fetchCompanies({ cognitoId, token }));
//       setFetchAttempted(true);
//     }
//   }, [dispatch, cognitoId, token]);

//   useEffect(() => {
//     // When loading goes from true to false, mark fetch as completed
//     if (prevLoading.current && !loading) {
//       setHasFetchedOnce(true);
//     }
//     prevLoading.current = loading;
//   }, [loading]);
//   useEffect(() => {
//     localStorage.removeItem("selectedCompanyId");
//     localStorage.removeItem("selectedUserCompany");
//     dispatch(resetCompanyState());
//   }, [dispatch]);

//   useEffect(() => {
//     if (companies.length > 0) {
//       const sortedCompanies = [...companies].sort((a, b) => a.name.localeCompare(b.name));
//       const newDisplayedCompanies = sortedCompanies.slice(0, page * COMPANIES_PER_PAGE);
//       setDisplayedCompanies(newDisplayedCompanies);
//       setHasMore(newDisplayedCompanies.length < sortedCompanies.length);
//     }
//   }, [companies, page]);

//   const handleSelectCompany = (companyId: string | number) => {
//     dispatch(resetCompanyState());
//     localStorage.setItem("selectedCompanyId", companyId.toString());
//     console.log("Selected company:", companyId);

//     // Pass an object to fetchUserCompany
//     dispatch(fetchUserCompany({ company_id: companyId, token }));
//     navigate("/overview");
//   };

//   // if (loading && displayedCompanies.length === 0) {
//   //   return <Loading />;
//   // }

//   const filteredCompanies = searchTerm
//     ? companies
//         .filter((company) => {
//           const search = searchTerm.toLowerCase();
//           const companyName = company.name.toLowerCase();
//           const companyId = String(company.id).toLowerCase();

//           return companyName.includes(search) || companyId.startsWith(search);
//         })
//         .sort((a, b) => a.name.localeCompare(b.name))
//     : displayedCompanies;

//   if (loading || !fetchAttempted) {
//     return <Loading />;
//   }

//   // Only show "No Companies Available" if fetch has completed and companies is empty
//   if (hasFetchedOnce && companies.length === 0) {
//     return (
//       <div className="flex flex-col items-center mt-32 ">
//         <div className="lg:w-[700px]">
//           <div className="p-5 border border-[#FED7AA] rounded-md bg-[#FFEDD5] text-[#C2410C] text-sm">
//             <p className="font-semibold mb-2"> {t("noCompanyTitle")}</p>
//             <p className="mb-4">{t("noCompanyDescription")}</p>
//             <NavLink
//               to="/add-company"
//               className="inline-block bg-[#C2410C] text-white py-2 px-4 rounded-md text-sm hover:bg-[#9A3412] transition-colors"
//             >
//               {t("addCompanyButton")}
//             </NavLink>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div>
//       <div className="lg:w-[700px]  mt-[7.8rem] mx-auto ">
//         <NavLink
//           to="/add-company"
//           className="bg-primary-600 dark:bg-primary-800 text-white py-[0.78rem] px-[1.5rem] rounded-[0.25rem] text-[0.875rem]"
//         >
//           {t("addCompany")}
//         </NavLink>
//       </div>

//       <div className="lg:w-[700px] border-[1px] dark:border-primary-800 mt-12 mb-[7.8rem] mx-auto rounded-lg">
//         <div className="bg-primary-600 dark:bg-primary-800 px-8 py-7 text-white rounded-t-lg">
//           <h1 className="font-semibold text-xl">{t("selectCompany")}</h1>
//         </div>
//         <div className="bg-white dark:bg-primary-900 px-8 p-7 rounded-b-lg">
//           <SearchInput
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             placeholder={t("searchCompany")}
//             className="mb-8"
//           />
//           <div className="max-h-[380px] overflow-y-auto" id="scrollableDiv">
//             {filteredCompanies.map((company, index) => (
//               <div
//                 className="flex justify-between items-center bg-gray-50 dark:bg-primary-950 rounded-[4px] border-[1px] dark:border-primary-800 px-6 py-4 mb-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-primary-900"
//                 key={company.id}
//                 onClick={() => handleSelectCompany(company.id)}
//                 ref={index === filteredCompanies.length - 1 ? lastCompanyElementRef : null}
//               >
//                 <div>
//                   <h2 className="text-primary-600 dark:text-white mb-[3px] font-semibold">{company.name}</h2>
//                   <p className="text-primary-400 dark:text-primary-300">#{company.id}</p>
//                 </div>
//                 <div>
//                   <button>
//                     <RightEmptyArrow />
//                   </button>
//                 </div>
//               </div>
//             ))}
//             {loading && <Loading />}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SelectCompanyPage;
